import { useMutation, useQuery } from '@apollo/client';
import {
  Mutation,
  MutationBlockNfcCardArgs,
  Query,
  QueryGetVisitorByIdArgs,
  Subscription,
  SubscriptionOnMobileDeviceRegisteredByIdArgs,
  SubscriptionOnRegistrationTokenSavedByIdArgs
} from 'graphql/generated/graphqlTypes';
import { BLOCK_NFC_CARD } from 'graphql/mutations';
import { GET_CARDS } from 'graphql/queries';
import { ON_MOBILE_DEVICE_REGISTERED_BY_ID, ON_REGISTRATION_TOKEN_SAVED_BY_ID } from 'graphql/subscriptions';
import { GET_VISITOR_DETAILS } from 'graphql/v2/queries';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import { useEffect, useState } from 'react';
import { getCardsByVisitorId } from 'utils/NfcCard/nfcCard';
import {
  getVisitorMobileAccessAppStatus,
  getVisitorMobileConfigAppStatus,
  isVisitorHasInstallerRole
} from 'utils/Visitor/visitor';
import { TranslatedHookProps } from '../../../shared/types';

interface Props extends TranslatedHookProps {
  visitorId: string;
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 6000;

const useAccess = ({ handleFetchError, translation, visitorId }: Props) => {
  const {
    loading: visitorDetailsLoading,
    data: visitorDetailsData,
    error: visitorDetailsError,
    subscribeToMore: subscribeToMoreVisitorDetails,
    refetch: refetchVisitorDetails,
    startPolling: startPollingVisitorDetails,
    stopPolling: stopPollingVisitorDetails
  } = useQuery<Pick<Query, 'getVisitorById'>, QueryGetVisitorByIdArgs>(GET_VISITOR_DETAILS, {
    variables: { id: visitorId }
  });

  const visitorDetails = visitorDetailsData?.getVisitorById;

  useEffect(() => {
    if (visitorDetailsError) {
      handleFetchError(translation.user_details_error_fetching);
      console.error(visitorDetailsError);
    }
  }, [visitorDetailsError]);

  const rawMobileDeviceConfigApp = visitorDetails ? getVisitorMobileConfigAppStatus(visitorDetails) : undefined;
  const rawMobileDeviceAccessApp = visitorDetails ? getVisitorMobileAccessAppStatus(visitorDetails) : undefined;
  const hasInstallerRole = !!visitorDetails && isVisitorHasInstallerRole(visitorDetails);

  // Both mobile devices rows can briefly disappear because of a stale/out-of-order background response
  // (polling and the mobile-device-registered subscription both refetch this query independently).
  // Once we've received a device data, keep showing it rather than flickering back to "waiting". The CONFIG
  // device is also gated on the installer role, so losing the role or switching visitor clears it.
  const [mobileDeviceConfigApp, setMobileDeviceConfigApp] = useState(rawMobileDeviceConfigApp);
  const [mobileDeviceAccessApp, setMobileDeviceAccessApp] = useState(rawMobileDeviceAccessApp);

  useEffect(() => {
    if (!visitorDetails || !hasInstallerRole) {
      setMobileDeviceConfigApp(undefined);
      return;
    }

    if (rawMobileDeviceConfigApp) setMobileDeviceConfigApp(rawMobileDeviceConfigApp);
  }, [visitorDetails, hasInstallerRole, rawMobileDeviceConfigApp]);

  useEffect(() => {
    if (!visitorDetails) {
      setMobileDeviceAccessApp(undefined);
      return;
    }

    if (rawMobileDeviceAccessApp) setMobileDeviceAccessApp(rawMobileDeviceAccessApp);
  }, [visitorDetails, rawMobileDeviceAccessApp]);

  const isWaitingForConfigDevice = hasInstallerRole && !mobileDeviceConfigApp;

  // The device just disconnected is unregistered asynchronously on the backend, so the refetch that
  // follows the mutation can still return isRegistered: true. Track it and poll briefly until it flips.
  const [awaitingDisconnectDeviceId, setAwaitingDisconnectDeviceId] = useState<string | null>(null);

  useEffect(() => {
    if (!awaitingDisconnectDeviceId) return;

    // A device missing from a given raw response can be a stale/out-of-order background response (see
    // comment above), not a real state change, so only stop waiting on a confirmed isRegistered: false.
    const matchedDevice =
      (rawMobileDeviceAccessApp?.id === awaitingDisconnectDeviceId && rawMobileDeviceAccessApp) ||
      (rawMobileDeviceConfigApp?.id === awaitingDisconnectDeviceId && rawMobileDeviceConfigApp) ||
      undefined;

    if (matchedDevice && !matchedDevice.isRegistered) setAwaitingDisconnectDeviceId(null);
  }, [awaitingDisconnectDeviceId, rawMobileDeviceAccessApp, rawMobileDeviceConfigApp]);

  const isPolling = isWaitingForConfigDevice || !!awaitingDisconnectDeviceId;

  // Polls while either: the CONFIG mobile device row is provisioned asynchronously after a visitor becomes
  // an installer, so it may not exist yet on the refetch that follows the edit; or a device disconnect is
  // awaiting backend confirmation (see awaitingDisconnectDeviceId above). Poll briefly until it resolves.
  useEffect(() => {
    if (!isPolling) {
      stopPollingVisitorDetails();
      return;
    }

    startPollingVisitorDetails(POLL_INTERVAL_MS);

    const timeout = setTimeout(() => {
      stopPollingVisitorDetails();
      setAwaitingDisconnectDeviceId(null);
    }, POLL_TIMEOUT_MS);

    return () => {
      clearTimeout(timeout);
      stopPollingVisitorDetails();
    };
  }, [isPolling, startPollingVisitorDetails, stopPollingVisitorDetails]);

  useRegisterBackgroundRefetch(isPolling);

  useEffect(() => {
    const unsubscribeFromMobileDeviceRegistered = subscribeToMoreVisitorDetails<
      Pick<Subscription, 'onMobileDeviceRegisteredById'>,
      SubscriptionOnMobileDeviceRegisteredByIdArgs
    >({
      document: ON_MOBILE_DEVICE_REGISTERED_BY_ID,
      variables: { visitorId },
      updateQuery: (prev) => {
        refetchVisitorDetails();
        return prev;
      }
    });

    const unsubscribeFromRegistrationTokenSaved = subscribeToMoreVisitorDetails<
      Pick<Subscription, 'onRegistrationTokenSavedById'>,
      SubscriptionOnRegistrationTokenSavedByIdArgs
    >({
      document: ON_REGISTRATION_TOKEN_SAVED_BY_ID,
      variables: { visitorId },
      updateQuery: (prev) => {
        refetchVisitorDetails();
        return prev;
      }
    });

    return () => {
      unsubscribeFromMobileDeviceRegistered();
      unsubscribeFromRegistrationTokenSaved();
    };
  }, [visitorId, subscribeToMoreVisitorDetails, refetchVisitorDetails]);

  const {
    loading: nfcCardsLoading,
    data: nfcCardsData,
    error: nfcCardsError
  } = useQuery<Pick<Query, 'getNfcCards'>>(GET_CARDS);

  useEffect(() => {
    if (nfcCardsError) {
      handleFetchError(translation.nfc_cards_error_fetching);
      console.error(nfcCardsError);
    }
  }, [nfcCardsError]);

  const [blockNfcCardMutation, { loading: blockNfcCardLoading, data: blockNfcCardData }] = useMutation<
    Pick<Mutation, 'blockNfcCard'>,
    MutationBlockNfcCardArgs
  >(BLOCK_NFC_CARD, {
    onError: (error) => {
      handleFetchError(translation.access_card_error_blocking);
      console.error(error);
    }
  });

  const blockNfcCard = async (reason: string, uuid: string) => {
    await blockNfcCardMutation({
      variables: {
        reason,
        uuid
      }
    });
  };

  const startAwaitingDisconnect = (mobileDeviceId: string) => setAwaitingDisconnectDeviceId(mobileDeviceId);

  return {
    visitorDetails,
    loadingVisitorDetails: visitorDetailsLoading,
    isWaitingForConfigDevice,
    awaitingDisconnectDeviceId,
    startAwaitingDisconnect,
    mobileDeviceConfigApp,
    mobileDeviceAccessApp,
    visitorCards: getCardsByVisitorId(visitorId, nfcCardsData?.getNfcCards || []),
    loadingVisitorCards: nfcCardsLoading,
    blockNfcCard,
    blockNfcCardLoading
  };
};

export default useAccess;
